import {
  U3204_ASSISTANT_PLACEHOLDER_ID,
  type U3204DisplayMessage,
  type U3204StreamState,
  type U3204ToolActivityItem,
} from '../U3204Service';

export function u3204UpsertToolActivityItem(
  items: U3204ToolActivityItem[] | undefined,
  toolName: string,
  status: U3204ToolActivityItem['status'],
): U3204ToolActivityItem[] {
  const next = [...(items ?? [])];
  const index = next.findIndex((item) => item.toolName === toolName);
  if (index === -1) {
    next.push({ toolName, status });
    return next;
  }
  next[index] = { toolName, status };
  return next;
}

export function u3204IsPendingAssistant(message: U3204DisplayMessage): boolean {
  return (
    message.id === U3204_ASSISTANT_PLACEHOLDER_ID ||
    (message.role === 'assistant' && message.generationStatus === 'pending')
  );
}

export function u3204HasPendingAssistant(messages: U3204DisplayMessage[]): boolean {
  return messages.some(u3204IsPendingAssistant);
}

export function u3204ResolveStreamStateAfterRefresh(params: {
  silent: boolean;
  hasPendingApi: boolean;
  hasPendingMerged: boolean;
}): U3204StreamState {
  if (params.hasPendingApi || (params.silent && params.hasPendingMerged)) {
    return 'streaming';
  }
  return 'idle';
}

function textLength(value: string | null | undefined): number {
  return value?.length ?? 0;
}

export interface U3204MergeClientMessageOptions {
  preserveLocalStream?: boolean;
}

export function u3204MergeClientMessageFields(
  previous: U3204DisplayMessage[],
  fromApi: U3204DisplayMessage[],
  options?: U3204MergeClientMessageOptions,
): U3204DisplayMessage[] {
  const previousById = new Map(previous.map((message) => [message.id, message]));
  const preserve = options?.preserveLocalStream === true;

  const merged = fromApi.map((message) => {
    const prev = previousById.get(message.id);
    const toolActivity = prev?.toolActivity?.length ? prev.toolActivity : message.toolActivity;

    if (!prev) {
      return message;
    }

    const keepLocalContent =
      preserve && u3204IsPendingAssistant(prev) && textLength(prev.content) > textLength(message.content);
    const keepLocalThinking =
      preserve &&
      u3204IsPendingAssistant(prev) &&
      textLength(prev.thinkingContent) > textLength(message.thinkingContent);

    return {
      ...message,
      content: keepLocalContent ? prev.content : message.content,
      thinkingContent: keepLocalThinking ? prev.thinkingContent : (message.thinkingContent ?? prev.thinkingContent),
      generationStatus: keepLocalContent ? prev.generationStatus : message.generationStatus,
      toolActivity,
      inputTokens: message.inputTokens ?? prev.inputTokens,
      outputTokens: message.outputTokens ?? prev.outputTokens,
      sources: message.sources ?? prev.sources,
    };
  });

  if (!preserve) {
    return merged;
  }

  const apiIds = new Set(fromApi.map((message) => message.id));
  const localOnlyPending = previous.filter(
    (message) => !apiIds.has(message.id) && u3204IsPendingAssistant(message),
  );

  const placeholder = localOnlyPending.find((message) => message.id === U3204_ASSISTANT_PLACEHOLDER_ID);
  const pendingApiIndex = merged.findIndex(
    (message) => message.id !== U3204_ASSISTANT_PLACEHOLDER_ID && u3204IsPendingAssistant(message),
  );

  if (placeholder && pendingApiIndex >= 0) {
    const apiPending = merged[pendingApiIndex];
    const folded: U3204DisplayMessage = {
      ...apiPending,
      content:
        textLength(placeholder.content) > textLength(apiPending.content)
          ? placeholder.content
          : apiPending.content,
      thinkingContent:
        textLength(placeholder.thinkingContent) > textLength(apiPending.thinkingContent)
          ? placeholder.thinkingContent
          : (apiPending.thinkingContent ?? placeholder.thinkingContent),
    };
    const withoutPlaceholder = localOnlyPending.filter(
      (message) => message.id !== U3204_ASSISTANT_PLACEHOLDER_ID,
    );
    const nextMerged = [...merged];
    nextMerged[pendingApiIndex] = folded;
    return withoutPlaceholder.length ? [...nextMerged, ...withoutPlaceholder] : nextMerged;
  }

  return localOnlyPending.length ? [...merged, ...localOnlyPending] : merged;
}
